import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enSidebar from './locales/en/sidebar.json';
import enMaintenance from './locales/en/maintenance.json';
import enTrading from './locales/en/trading.json';
import enAccounts from './locales/en/accounts.json';
import enProfile from './locales/en/profile.json';
import enMobile from './locales/en/mobile.json';
import enReports from './locales/en/reports.json';
import enAnalytics from './locales/en/analytics.json';
import enCrm from './locales/en/crm.json';
import arCommon from './locales/ar/common.json';
import arAuth from './locales/ar/auth.json';
import arSidebar from './locales/ar/sidebar.json';
import arMaintenance from './locales/ar/maintenance.json';
import arTrading from './locales/ar/trading.json';
import arAccounts from './locales/ar/accounts.json';
import arProfile from './locales/ar/profile.json';
import arMobile from './locales/ar/mobile.json';
import arReports from './locales/ar/reports.json';
import arAnalytics from './locales/ar/analytics.json';
import arCrm from './locales/ar/crm.json';
import frCommon from './locales/fr/common.json';
import frAuth from './locales/fr/auth.json';
import frSidebar from './locales/fr/sidebar.json';
import frMaintenance from './locales/fr/maintenance.json';
import frTrading from './locales/fr/trading.json';
import frAccounts from './locales/fr/accounts.json';
import frProfile from './locales/fr/profile.json';
import frMobile from './locales/fr/mobile.json';
import frReports from './locales/fr/reports.json';
import frAnalytics from './locales/fr/analytics.json';
import frCrm from './locales/fr/crm.json';

const LANGUAGE_STORAGE_KEY = 'quantix-language';
const SUPPORTED_LANGS = ['en', 'ar', 'fr'] as const;

function getStoredLanguage(): string | undefined {
    if (typeof window === 'undefined') return undefined;
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored && SUPPORTED_LANGS.includes(stored as any) ? stored : undefined;
}

const resources = {
    en: {
        common: enCommon as Record<string, string>,
        auth: enAuth as Record<string, string>,
        sidebar: enSidebar as Record<string, string>,
        maintenance: enMaintenance as Record<string, string>,
        trading: enTrading as Record<string, string>,
        accounts: enAccounts as Record<string, string>,
        profile: enProfile as Record<string, string>,
        mobile: enMobile as Record<string, string>,
        reports: enReports as Record<string, string>,
        analytics: enAnalytics as Record<string, string>,
        crm: enCrm as Record<string, any>,
    },
    ar: {
        common: arCommon as Record<string, string>,
        auth: arAuth as Record<string, string>,
        sidebar: arSidebar as Record<string, string>,
        maintenance: arMaintenance as Record<string, string>,
        trading: arTrading as Record<string, string>,
        accounts: arAccounts as Record<string, string>,
        profile: arProfile as Record<string, string>,
        mobile: arMobile as Record<string, string>,
        reports: arReports as Record<string, string>,
        analytics: arAnalytics as Record<string, string>,
        crm: arCrm as Record<string, any>,
    },
    fr: {
        common: frCommon as Record<string, string>,
        auth: frAuth as Record<string, string>,
        sidebar: frSidebar as Record<string, string>,
        maintenance: frMaintenance as Record<string, string>,
        trading: frTrading as Record<string, string>,
        accounts: frAccounts as Record<string, string>,
        profile: frProfile as Record<string, string>,
        mobile: frMobile as Record<string, string>,
        reports: frReports as Record<string, string>,
        analytics: frAnalytics as Record<string, string>,
        crm: frCrm as Record<string, any>,
    },
};

i18n.use(initReactI18next).init({
    resources,
    lng: getStoredLanguage(),
    fallbackLng: 'en',
    supportedLngs: ['en', 'ar', 'fr'],
    defaultNS: 'common',
    ns: [
        'common',
        'auth',
        'sidebar',
        'maintenance',
        'trading',
        'accounts',
        'profile',
        'mobile',
        'reports',
        'analytics',
        'crm',
    ],
    interpolation: {
        escapeValue: false,
    },
});

/** Languages written right to left. */
const RTL_LANGS = new Set(['ar', 'he', 'fa', 'ur']);

export function isRtlLanguage(lng?: string | null): boolean {
    if (!lng) return false;
    return RTL_LANGS.has(lng.split('-')[0]);
}

/**
 * Mirrors the document for RTL languages.
 *
 * Setting `dir` on <html> is what makes the browser flip logical CSS properties
 * (`ms-*`, `me-*`, `ps-*`, `pe-*`, `start`/`end` insets) and text alignment.
 * Physical utilities such as `ml-*`/`mr-*` are NOT flipped, which is why the CRM
 * is migrating to the logical ones.
 */
function applyDocumentDirection(lng: string): void {
    if (typeof document === 'undefined') return;
    const base = lng.split('-')[0];
    document.documentElement.lang = base;
    document.documentElement.dir = isRtlLanguage(base) ? 'rtl' : 'ltr';
}

applyDocumentDirection(i18n.language || 'en');

i18n.on('languageChanged', (lng: string) => {
    applyDocumentDirection(lng);

    if (typeof window === 'undefined') return;
    const base = lng.split('-')[0];
    if (SUPPORTED_LANGS.includes(base as (typeof SUPPORTED_LANGS)[number])) {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, base);
    }
});

export default i18n;
